import { toJS } from "mobx";
import { ILog } from "./logging";
import { isWindowDefined } from "./window";
import { isObjectEmpty } from "../utils/is-object-empty";

const log = ILog.logger("igloo-payload");

export interface AnalyticsEvent {
  sectionName: string;
  pageName: string;
  context?: string;
  action: AnalyticsEventAction;
  url?: string;
  referrer?: string;
}

export interface UploadEvent extends AnalyticsEvent {
  data: { [key: string]: any };
}

export type AnalyticsEventAction =
  | "share" // not valid type
  | "page_view"
  | "contest_open" // not valid type
  | "contest_exit" // not valid type
  | "item_selected"
  | "click";

export type ContestExitType = "dismiss" | "entry_success" | "entry_failure";

export interface IglooUser {
  profileId?: string;
  registration?: {
    birthYear?: number | null;
    gender?: string;
    type?: string;
    zip?: string;
  };
  privacyOptOut?: boolean;
  abTestGroup?: string[] | null;
  isTrialEligible?: boolean;
}

export interface IglooView {
  contentId?: string;
  pageURL: string;
  pageName?: string; // Required for click events
  asset?: {
    id: string;
    name: string;
  };
  filter?: {
    name: string;
    type: string;
  };
  authorId?: string;
  pubDate?: string;
  contentOrigin?: string;
  contentOriginType?: string;
  contentFrame?: string;
  stationCallLetter?: string;
  stationFormat?: string | null;
  stationMarket?: string | null;
  stationMicrosite?: string;
  tags?: string[] | string;
  topics?: string[];
  personalityId?: string;
  photoGalleryExists?: boolean;
  photoGalleryPageView?: boolean;
  pageLoadTime?: number;
}

export interface IglooEvent {
  location?: string;
}

export interface IglooShare {
  network?: string;
  url?: string;
}

export interface IglooAsset {
  id: string;
  name: string;
}

export interface IglooStation {
  asset?: IglooAsset;
}

export interface IglooItem {
  asset?: IglooAsset;
}

export interface IglooContest {
  exitType: ContestExitType;
}

export interface IglooTrackPayload {
  pageName: string; // trackClickAction, trackContestAction, trackShareAction, collectData, RSS feedScripts
  user?: IglooUser; // collectData
  event?: IglooEvent; // trackClickAction, collectData
  share?: IglooShare; // trackShareAction
  station?: IglooStation; // trackClickAction, trackShareAction
  item?: IglooItem; // trackClickAction
  view?: IglooView; // trackClickAction, trackContestAction, trackShareAction, collectData, RSS feedScripts
  contest?: IglooContest; // trackContestAction
  window?: {
    // trackClickAction - required by click event schema
    location: {
      href: string;
    };
  };
}

/**
 * Decode base64 contentId (IHRAL-6655)
 */
const decodeContentId = (contentId?: string): string | undefined => {
  if (!contentId) return undefined;
  // Reverse the base64 encoding of the contentId - IHRAL-6655
  try {
    return isWindowDefined() && typeof atob !== "undefined"
      ? atob(contentId)
      : Buffer.from(contentId, "base64").toString("utf-8");
  } catch (error) {
    log.error("Failed to decode contentId", error);
    return contentId;
  }
};

/**
 * Builds the Igloo payload from an UploadEvent
 * This builds a single factory payload for all igloo events.. which is what we did for adobe
 * This is fine for now for the initial launch, but if we want to enforce stricter typing per event type, we will need to create seperate factories
 */
export const buildIglooPayload = (event: UploadEvent, url?: string): IglooTrackPayload => {
  const { data, pageName } = event;

  // Convert MobX observables to plain JS objects using JSON round-trip
  // toJS() wasn't properly converting nested Proxy objects, so we use JSON serialization
  let dataObj;
  try {
    dataObj = JSON.parse(JSON.stringify(toJS(data)));
  } catch (error) {
    log.error("Failed to serialize igloo data", error);
    dataObj = {};
  }

  const payload: IglooTrackPayload = {
    pageName,
  };

  if (!isObjectEmpty(dataObj.event)) {
    payload.event = { ...dataObj.event };
  }

  if (!isObjectEmpty(dataObj.share)) {
    payload.share = { ...dataObj.share };
  }

  if (!isObjectEmpty(dataObj.station)) {
    payload.station = { ...dataObj.station };
  }

  if (!isObjectEmpty(dataObj.item)) {
    payload.item = { ...dataObj.item };
  }

  if (!isObjectEmpty(dataObj.window)) {
    payload.window = { ...dataObj.window };
  }

  if (!isObjectEmpty(dataObj.view)) {
    payload.view = {
      ...dataObj.view,
      pageName,
      contentId: decodeContentId(dataObj.view.contentId),
      pageURL: url || (isWindowDefined() ? window.location.href : ""),
    };
  }

  if (!isObjectEmpty(dataObj.contest)) {
    payload.contest = { ...dataObj.contest };
  }

  return payload;
};

export const getDeviceHost = (market?: string): string => {
  const normalizedMarket = market?.toLowerCase().trim();
  const hostPrefix = normalizedMarket === "premiere" ? "premiere" : "local";
  return hostPrefix + ".inferno.us";
};

/**
 * Get the page URL (server/client safe)
 */
export const getPageURL = (): string => {
  return isWindowDefined() ? window.location.href : "";
};

/**
 * Get the referring URL from document.referrer
 * Note: This will be empty if user lands directly or if the referring site uses rel="noreferrer"
 */
export const getReferer = (): string | undefined => {
  if (!isWindowDefined()) return undefined;
  const referrer = document.referrer;
  return referrer || undefined;
};

export const getDeviceData = <T extends Record<string, any>>(
  device?: T,
  deviceId?: string,
  refererOverride?: string,
): T & { referer?: string } & (string extends typeof deviceId ? {} : { id: string }) => {
  const referer = refererOverride || getReferer();

  return {
    ...(device || ({} as T)),
    ...(deviceId ? { id: deviceId } : {}),
    ...(referer ? { referer } : {}),
  } as any;
};
